// Helpers purs de la section « Sécurité » du back-office.
//
// Tout ce qui se teste sans navigateur vit ici : validation d'une IP saisie à la
// main, mise en forme des compteurs et des dates, tri des tableaux « top », mise
// en forme de la série pour le graphe et libellés français des types d'alerte.
// Les composants React ne gardent que le rendu et les appels réseau.
//
// Contrat des endpoints back_api (cf. plan « security-watch », lot C) :
//   GET  /security/overview?period=24h|7d|30d
//   GET  /security/alerts?limit=200          POST /security/alerts/ack
//   GET|POST|DELETE /security/blocks
// Toutes les listes peuvent être absentes ou vides : les helpers renvoient
// toujours une valeur affichable (jamais d'exception sur une donnée manquante).

// Périodes proposées par le sélecteur de la vue d'ensemble.
export const SECURITY_PERIODS = [
    { value: "24h", label: "24 h" },
    { value: "7d", label: "7 j" },
    { value: "30d", label: "30 j" },
];

// Durées de blocage acceptées par `POST /security/blocks`.
export const BLOCK_DURATIONS = [
    { value: "24h", label: "24 heures" },
    { value: "7d", label: "7 jours" },
    { value: "permanent", label: "Définitif" },
];

// --- Validation d'IP ---------------------------------------------------------

function isValidIPv4(value) {
    const parts = String(value).split(".");
    if (parts.length !== 4) return false;
    return parts.every((part) => {
        if (!/^[0-9]{1,3}$/.test(part)) return false;
        // Un zéro en tête est ambigu (lecture octale) : on refuse « 01.2.3.4 ».
        if (part.length > 1 && part[0] === "0") return false;
        return Number(part) <= 255;
    });
}

function isValidIPv6(value) {
    let v = String(value);

    // Forme mixte « ::ffff:192.168.0.1 » : on valide la queue IPv4 puis on la
    // remplace par deux groupes hexa pour compter les groupes normalement.
    if (v.indexOf(".") !== -1) {
        const lastColon = v.lastIndexOf(":");
        if (lastColon === -1) return false;
        if (!isValidIPv4(v.slice(lastColon + 1))) return false;
        v = v.slice(0, lastColon + 1) + "0:0";
    }

    if (!/^[0-9A-Fa-f:]+$/.test(v)) return false;

    const group = (g) => /^[0-9A-Fa-f]{1,4}$/.test(g);
    const split = (s) => (s === "" ? [] : s.split(":"));

    const halves = v.split("::");
    if (halves.length > 2) return false; // une seule compression autorisée

    if (halves.length === 2) {
        const left = split(halves[0]);
        const right = split(halves[1]);
        if (!left.every(group) || !right.every(group)) return false;
        // La compression doit remplacer au moins un groupe.
        return left.length + right.length <= 7;
    }

    const groups = split(v);
    return groups.length === 8 && groups.every(group);
}

// isValidIp : IPv4 ou IPv6, adresse SEULE (pas de CIDR, pas de port). Le back
// pose lui-même le /32 ou le /128 dans l'IP set WAF, pikachu n'envoie que l'IP.
export function isValidIp(value) {
    const v = String(value === null || value === undefined ? "" : value).trim();
    if (v === "") return false;
    if (v.indexOf("/") !== -1) return false; // CIDR refusé
    if (v.indexOf(" ") !== -1) return false;
    return v.indexOf(":") !== -1 ? isValidIPv6(v) : isValidIPv4(v);
}

// --- Mise en forme -----------------------------------------------------------

// formatCount : entier séparé par milliers (espace insécable), « — » si absent.
export function formatCount(value) {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    if (!isFinite(n)) return "—";
    const rounded = Math.round(Math.abs(n));
    const digits = String(rounded).replace(/\B(?=([0-9]{3})+(?![0-9]))/g, " ");
    return (n < 0 ? "-" : "") + digits;
}

const RFC3339 = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2})/;

// formatDateTime : date lisible en heure de Paris (« 15/09/2026 12:05 »).
export function formatDateTime(value) {
    const v = String(value === null || value === undefined ? "" : value).trim();
    if (v === "" || !RFC3339.test(v)) return "—";
    const date = new Date(v);
    if (isNaN(date.getTime()) || date.getFullYear() < 1971) return "—";
    try {
        return date.toLocaleString("fr-FR", {
            timeZone: "Europe/Paris",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return v;
    }
}

// durationLabel : libellé d'une durée de blocage (valeur inconnue rendue telle
// quelle plutôt que masquée — on préfère voir une valeur inattendue).
export function durationLabel(duration) {
    const v = String(duration === null || duration === undefined ? "" : duration).trim();
    const found = BLOCK_DURATIONS.find((d) => d.value === v);
    if (found) return found.label;
    return v === "" ? "—" : v;
}

const ALERT_TYPE_LABELS = {
    waf_total: "Pic de requêtes bloquées (WAF)",
    waf_ip: "IP hostile (WAF)",
    api_ip: "Erreurs API depuis une IP",
    api_5xx: "Pic d'erreurs serveur (5xx)",
    login: "Échecs de connexion au back-office",
    login_ip: "Échecs de connexion depuis une IP",
    guardduty: "Détection GuardDuty",
    cloudtrail: "Action sensible AWS (CloudTrail)",
    source: "Source de journaux muette",
};

// alertTypeLabel : libellé français d'un type d'alerte. Un type inconnu (robot
// plus récent que le back-office) est affiché brut, jamais avalé.
export function alertTypeLabel(type) {
    const v = String(type === null || type === undefined ? "" : type).trim();
    if (v === "") return "—";
    return ALERT_TYPE_LABELS[v] || v;
}

// alertTypeColor : couleur du badge. Les constats GuardDuty/CloudTrail sont les
// plus graves (compte AWS), le reste est du bruit d'attaque côté périmètre.
export function alertTypeColor(type) {
    const v = String(type === null || type === undefined ? "" : type).trim();
    if (v === "guardduty" || v === "cloudtrail") return "danger";
    if (v === "source") return "secondary";
    return "warning";
}

// --- Tableaux « top » --------------------------------------------------------

// sortTop : tri décroissant par une clé numérique, sans muter la liste reçue.
// Les entrées non numériques comptent pour zéro (l'API peut omettre `count`).
export function sortTop(list, key) {
    const rows = Array.isArray(list) ? list.filter((row) => row && typeof row === "object") : [];
    const value = (row) => {
        const n = Number(row[key]);
        return isFinite(n) ? n : 0;
    };
    return [...rows].sort((a, b) => value(b) - value(a));
}

// --- Série du graphe ---------------------------------------------------------

// seriesLabel : « 15/09 10 h » pour une série horaire, « 15/09 » pour une série
// journalière. On lit la chaîne RFC3339 telle quelle (UTC, comme l'API) plutôt
// que de passer par Date : le libellé doit rester aligné sur les fenêtres
// d'agrégation du robot, pas sur le fuseau du navigateur.
function seriesLabel(value, daily) {
    const match = RFC3339.exec(String(value === null || value === undefined ? "" : value).trim());
    if (!match) return "—";
    const day = match[3] + "/" + match[2];
    return daily ? day : day + " " + match[4] + " h";
}

const DATASET_STYLE = [
    { key: "blocked", label: "Requêtes bloquées", color: "#d9534f" },
    { key: "counted", label: "Requêtes comptées (mode compter)", color: "#f0ad4e" },
    { key: "apiErrors", label: "Erreurs API", color: "#3B7DDD" },
];

// seriesToChart : `series` de l'API → données chart.js (labels + 3 courbes).
// La granularité vient des données (horaire sur 24 h / 7 j, journalière sur
// 30 j) : si tous les points tombent à minuit, on n'affiche que la date.
export function seriesToChart(series) {
    const rows = Array.isArray(series) ? series.filter((row) => row && typeof row === "object") : [];
    const daily =
        rows.length > 0 &&
        rows.every((row) => {
            const match = RFC3339.exec(String(row.hour || ""));
            return match && match[4] === "00" && match[5] === "00";
        });

    const num = (v) => {
        const n = Number(v);
        return isFinite(n) ? n : 0;
    };

    return {
        labels: rows.map((row) => seriesLabel(row.hour, daily)),
        datasets: DATASET_STYLE.map((style) => ({
            label: style.label,
            fill: false,
            spanGaps: true,
            backgroundColor: "transparent",
            borderColor: style.color,
            pointRadius: rows.length > 60 ? 0 : 2,
            data: rows.map((row) => num(row[style.key])),
        })),
    };
}

// --- Erreurs API -------------------------------------------------------------

// apiErrorMessage : message lisible d'une réponse d'erreur. Les endpoints
// sécurité renvoient `{"error":"…"}` en français (IP invalide, motif manquant,
// durée inconnue) — c'est ce texte-là qu'il faut montrer, mot pour mot.
export function apiErrorMessage(status, body) {
    const text = String(body === null || body === undefined ? "" : body).trim();
    let message = text;
    try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object") {
            if (typeof parsed.error === "string" && parsed.error.trim() !== "") {
                message = parsed.error.trim();
            } else if (typeof parsed.message === "string" && parsed.message.trim() !== "") {
                message = parsed.message.trim();
            }
        }
    } catch (e) {
        // Corps non JSON (page d'erreur de la gateway) : on garde le texte brut.
    }
    return message === "" ? "HTTP " + status : "HTTP " + status + " — " + message;
}
